import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DataTableHeaderActions } from 'app/components/StyledComponents';
import EventLog from 'app/apiService/eventLog/eventLogApiService';
import { EventLogTypeOption } from 'app/apiService/eventLog/eventLogApiService';

const SEARCH_DEBOUNCE_MS = 400;

export interface EventLogsFilters {
  search?: string;
  event_type?: string;
  status?: string;
}

export interface EventLogsFilterTrayProps {
  onApply: (filters: EventLogsFilters) => void;
  initialFilters?: EventLogsFilters;
  currentPage?: number;
  totalPages?: number;
}

const EventLogsFilterTray: React.FC<EventLogsFilterTrayProps> = ({
  onApply,
  initialFilters = {},
  currentPage = 0,
  totalPages = 0,
}) => {
  const [search, setSearch] = useState(initialFilters.search ?? '');
  const [eventType, setEventType] = useState(initialFilters.event_type ?? '');
  const [status, setStatus] = useState(initialFilters.status ?? '');
  const [eventTypes, setEventTypes] = useState<EventLogTypeOption[]>([]);
  const [statuses, setStatuses] = useState<EventLogTypeOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstSearchMount = useRef(true);

  useEffect(() => {
    EventLog.getEventLogTypes()
      .then(res => {
        if (res?.body && Array.isArray(res.body)) setEventTypes(res.body);
      })
      .catch(() => setEventTypes([]))
      .finally(() => setLoadingTypes(false));
  }, []);

  useEffect(() => {
    EventLog.getEventLogStatuses()
      .then(res => {
        if (res?.body && Array.isArray(res.body)) setStatuses(res.body);
      })
      .catch(() => setStatuses([]))
      .finally(() => setLoadingStatuses(false));
  }, []);

  const applyFilters = (eventTypeVal: string, statusVal: string, searchVal: string) => {
    const filters: EventLogsFilters = {};
    if (searchVal.trim()) filters.search = searchVal.trim();
    if (eventTypeVal) filters.event_type = eventTypeVal;
    if (statusVal) filters.status = statusVal;
    onApply(filters);
  };

  useEffect(() => {
    if (isFirstSearchMount.current) {
      isFirstSearchMount.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      applyFilters(eventType, status, search);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  const handleEventTypeChange = (value: string) => {
    setEventType(value);
    applyFilters(value, status, search);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    applyFilters(eventType, value, search);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleClear = () => {
    setSearch('');
    setEventType('');
    setStatus('');
    onApply({});
  };

  return (
    <DataTableHeaderActions
      sx={{
        height: 'auto',
        minHeight: 40,
        alignItems: 'stretch',
        marginBottom: 0,
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
        position: 'relative',
        zIndex: 1,
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          px: 0,
          py: 0.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FilterListIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography
            component="span"
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              lineHeight: 1,
            }}
          >
            Filters
          </Typography>
        </Box>
        {totalPages > 0 && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: 'action.hover',
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: '0.7rem',
                color: 'text.secondary',
                lineHeight: 1,
              }}
            >
              Page
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1,
              }}
            >
              {currentPage} of {totalPages}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'nowrap',
          width: '100%',
          py: 1,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by originating IP"
          value={search}
          onChange={e => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'action.active', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              minHeight: 40,
              fontSize: '14px',
              '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' },
            },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200, flexShrink: 0 }}>
          <InputLabel id="event-log-type-label">Event type</InputLabel>
          <Select
            labelId="event-log-type-label"
            value={eventType}
            label="Event type"
            onChange={e => handleEventTypeChange(e.target.value)}
            sx={{
              fontSize: '14px',
              minHeight: 40,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' },
            }}
            disabled={loadingTypes}
          >
            <MenuItem value="">All</MenuItem>
            {eventTypes.map(opt => (
              <MenuItem key={opt.key} value={opt.value}>
                {opt.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160, flexShrink: 0 }}>
          <InputLabel id="event-log-status-label">Status</InputLabel>
          <Select
            labelId="event-log-status-label"
            value={status}
            label="Status"
            onChange={e => handleStatusChange(e.target.value)}
            sx={{
              fontSize: '14px',
              minHeight: 40,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' },
            }}
            disabled={loadingStatuses}
          >
            <MenuItem value="">All</MenuItem>
            {statuses.map(opt => (
              <MenuItem key={opt.key} value={opt.value}>
                {opt.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="medium"
          onClick={handleClear}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            minHeight: 40,
            px: 2,
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          Clear
        </Button>
      </Box>
    </DataTableHeaderActions>
  );
};

export default EventLogsFilterTray;
